import {
  type CompleteFollowUpRequest,
  type LeadDetail,
  type LeadFollowUpResult,
  type MissingField,
  type ScheduleFollowUpRequest,
} from '@utazzo/shared';
import { UzButton, UzCard, UzInput, UzTextarea } from '@utazzo/ui';
import { useState } from 'react';

import { useScheduleFollowUp } from '../use-leads';

import { LeadMissingFieldsModal } from './LeadMissingFieldsModal';

import { extractServerError } from '@/lib/api-error';

interface FollowUpLogFormProps {
  lead: LeadDetail;
}

/// Follow-up channel is hardcoded to a phone call (2026-06-10 rev) -
/// the dropdown is hidden from the UI and we always submit `CALL`.
/// Other channels (WhatsApp / Email / In-person) can come back behind
/// a feature flag if the workflow needs them again.
const DEFAULT_KIND: ScheduleFollowUpRequest['kind'] = 'CALL';

/**
 * "Log a follow-up" panel that lives at the top of the Follow-up tab on the
 * lead detail page.
 *
 * Phase A1 scope: this form schedules a follow-up only - the result/outcome
 * flow (CONNECTED / NO_RESPONSE / DELAYED) is captured via the per-row
 * "Complete" action on the existing follow-ups in the timeline below. That
 * keeps the inline form short for the common case ("set a callback for
 * tomorrow at 4 pm") and concentrates the conditional-validation matrix
 * (call status + lost reason + Won fields, see ADR-0033) inside the
 * dedicated complete-follow-up dialog.
 */
export const FollowUpLogForm = ({ lead }: FollowUpLogFormProps): React.JSX.Element => {
  const schedule = useScheduleFollowUp();
  const [scheduledAt, setScheduledAt] = useState<string>(defaultScheduledAt());
  const [notes, setNotes] = useState('');
  // Travel-intent backlog gate (2026-08-10). When schedule fails with the
  // typed 400, we open the missing-fields modal and re-fire the SAME
  // scheduledAt / notes so the seller never retypes them.
  const [travelIntentGap, setTravelIntentGap] = useState<{
    missing: MissingField[];
  } | null>(null);
  // Default to the lead's currently-assigned agent. If none, fall back to the
  // currently-logged-in user via a placeholder - the server validates against
  // active users so unknown ids surface as a 400.
  const defaultAgentId = lead.assignedAgent?.id;

  const reset = (): void => {
    setScheduledAt(defaultScheduledAt());
    setNotes('');
  };

  const submit = async (): Promise<void> => {
    if (!defaultAgentId) {
      // Without an owner the server doesn't know who to chase. The detail
      // header surfaces this in a banner so we keep this form sub-flow short.
      return;
    }
    try {
      await schedule.mutateAsync({
        id: lead.id,
        payload: {
          scheduledAt: new Date(scheduledAt).toISOString(),
          kind: DEFAULT_KIND,
          agentId: defaultAgentId,
          notes: notes.trim() ? notes.trim() : null,
        },
      });
      reset();
    } catch (err) {
      const extracted = extractServerError(err);
      if (extracted.code === 'active_needs_travel_intent') {
        // Backlog gate. Keep the typed scheduledAt / notes intact so the
        // seller can re-submit with one click after filling the fields.
        const missing = ((err as { missing?: MissingField[] })?.missing ?? []) as MissingField[];
        setTravelIntentGap({ missing });
        return;
      }
      // onError handler in useScheduleFollowUp already shows the toast for
      // other error shapes.
    }
  };

  return (
    <UzCard
      title="Log a follow-up"
      description="Schedule the next touch with this customer. The lead's next-follow-up date is maintained automatically."
    >
      <div className="space-y-3">
        {!defaultAgentId && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Assign an owner to this lead first - the follow-up needs someone to make the call.
          </div>
        )}
        {/* Channel is hardcoded to a phone call - see DEFAULT_KIND. The
            datetime picker takes the slot where the dropdown used to
            live so the layout stays consistent with the rest of the
            detail page. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <UzInput
            name="scheduledAt"
            type="datetime-local"
            label="When"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            disabled={schedule.isPending}
          />
        </div>
        <UzTextarea
          name="notes"
          label="Notes"
          placeholder="What you plan to discuss, or context for whoever picks this up."
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={schedule.isPending}
        />
        <div className="flex justify-end">
          <UzButton
            onClick={submit}
            loading={schedule.isPending}
            disabled={!defaultAgentId || !scheduledAt}
          >
            Schedule follow-up
          </UzButton>
        </div>
      </div>

      {/* Travel-intent backlog modal. Opens when the schedule submit hits
          the typed 400. On save success we re-fire the pending submit so
          the seller does not retype the scheduledAt / notes. */}
      {travelIntentGap && (
        <LeadMissingFieldsModal
          open
          mode="travelIntent"
          lead={lead}
          onOpenChange={(open) => {
            if (!open) setTravelIntentGap(null);
          }}
          title="Fill travel-intent fields before scheduling"
          description="This lead was marked Active before we started capturing travel-intent inputs. Fill these to continue."
          onReady={() => {
            setTravelIntentGap(null);
            void submit();
          }}
        />
      )}
    </UzCard>
  );
};

/**
 * Default value for the datetime-local input - "tomorrow at 10:00 in the
 * browser's timezone". The server stores this in UTC; this is purely display.
 */
function defaultScheduledAt(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Static lookup of result options, exported so the complete-follow-up dialog
 * (which lives next to the timeline) can render the same set.
 */
export const FOLLOW_UP_RESULT_OPTIONS: ReadonlyArray<{
  value: LeadFollowUpResult;
  label: string;
}> = [
  { value: 'CONNECTED', label: 'Connected' },
  { value: 'NO_RESPONSE', label: 'No response' },
  { value: 'DELAYED', label: 'Delayed' },
  { value: 'COMPLETED', label: 'Completed' },
];

/**
 * Build a `CompleteFollowUpRequest` payload from raw form values. Centralised
 * so the dialog and any future reusable form share the same shape transform.
 */
export const buildCompletePayload = (input: {
  result: LeadFollowUpResult;
  callStatusId: string | null;
  /** ADR-0051. Child sub-status of `callStatusId`. */
  callSubStatusId?: string | null;
  /** ADR-0051. Required when the picked sub-status has
   *  `isRequireFollowUp = true`; rejected otherwise. */
  followupReasonId?: string | null;
  /** ADR-0053. Required when the picked sub-status has
   *  `isLostReason = true`; rejected otherwise. When set + valid,
   *  the server also auto-closes the lead to LOST in the same
   *  transaction. */
  lostReasonId?: string | null;
  notes: string;
  nextFollowUpAt: string | null;
}): CompleteFollowUpRequest => ({
  result: input.result,
  callStatusId: input.callStatusId,
  callSubStatusId: input.callSubStatusId ?? null,
  followupReasonId: input.followupReasonId ?? null,
  lostReasonId: input.lostReasonId ?? null,
  notes: input.notes.trim() ? input.notes.trim() : null,
  nextFollowUpAt: input.nextFollowUpAt ? new Date(input.nextFollowUpAt).toISOString() : null,
});
